import React, { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { 
  Map as MapIcon, 
  Plus, 
  Trash2, 
  Save, 
  MapPin, 
  Navigation,
  Target,
  TreePine,
  Milestone,
  Edit,
  Eye,
  EyeOff,
  Layers,
  Share2,
  Send,
  SlidersHorizontal,
  LocateFixed,
  QrCode,
  MessageSquare,
  Copy,
  Bell,
  Users
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { MapContainer, TileLayer, WMSTileLayer, Polygon, Marker, Popup, Polyline, useMapEvents, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import HighSeatsLayer, { HighSeatsAdminPanel } from "../components/HighSeatsLayer";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
} from "../components/ui/dropdown-menu";

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom icons
const createIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Create a text label icon for km stones with pointer
const createKmIcon = (text) => new L.DivIcon({
  className: 'leaflet-div-icon-transparent',
  html: `<div style="
    position: relative;
    display: inline-block;
  ">
    <div style="
      background: #f97316;
      color: white;
      padding: 3px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: bold;
      white-space: nowrap;
      border: 2px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.4);
    ">${text}</div>
    <div style="
      position: absolute;
      bottom: -8px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 8px solid #f97316;
    "></div>
  </div>`,
  iconSize: null,
  iconAnchor: [30, 35]
});

// Create a text label icon for local names
const createLabelIcon = (text) => new L.DivIcon({
  html: `<div style="
    background: rgba(37, 99, 235, 0.95);
    color: white;
    padding: 5px 10px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    border: 2px solid white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.4);
    display: inline-block;
  ">${text}</div>`,
  iconSize: null,
  iconAnchor: [0, 0],
  className: 'leaflet-div-icon-transparent'
});

// Create icon for other members' locations
const createMemberIcon = (name) => new L.DivIcon({
  className: 'leaflet-div-icon-transparent',
  html: `<div style="
    position: relative;
    display: inline-block;
  ">
    <div style="
      background: #7c3aed;
      color: white;
      padding: 3px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: bold;
      white-space: nowrap;
      border: 2px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.4);
    ">${name}</div>
    <div style="
      position: absolute;
      bottom: -6px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 6px solid #7c3aed;
    "></div>
  </div>`,
  iconSize: null,
  iconAnchor: [30, 30]
});

const icons = {
  stojisce: createIcon("green"),
  lokalno_ime: createIcon("blue"),
  km_kamen: createIcon("orange"),
  default: createIcon("red")
};

// Custom DivIcon for opazovalnica (observation tower)
const createOpazovalnicaIcon = () => new L.DivIcon({
  className: 'leaflet-div-icon-transparent',
  html: `<div style="position:relative;display:inline-block;">
    <div style="width:34px;height:34px;border-radius:50%;background:#0d9488;border:3px solid white;color:white;display:flex;align-items:center;justify-content:center;font-size:18px;box-shadow:0 2px 8px rgba(0,0,0,0.4);">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/><line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/></svg>
    </div>
    <div style="position:absolute;bottom:-8px;left:50%;transform:translateX(-50%);width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:8px solid #0d9488;"></div>
  </div>`,
  iconSize: null,
  iconAnchor: [17, 42]
});

// Custom DivIcon for krmišče (feeding station)
const createKrmisceIcon = () => new L.DivIcon({
  className: 'leaflet-div-icon-transparent',
  html: `<div style="position:relative;display:inline-block;">
    <div style="width:34px;height:34px;border-radius:50%;background:#b45309;border:3px solid white;color:white;display:flex;align-items:center;justify-content:center;font-size:18px;box-shadow:0 2px 8px rgba(0,0,0,0.4);">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="0"><path d="M12 2L4 8v2h16V8L12 2zM4 12v8h4v-6h8v6h4v-8H4z"/></svg>
    </div>
    <div style="position:absolute;bottom:-8px;left:50%;transform:translateX(-50%);width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:8px solid #b45309;"></div>
  </div>`,
  iconSize: null,
  iconAnchor: [17, 42]
});

// Custom DivIcon for lovska koča (hunting cabin)
const createLovskaKocaIcon = () => new L.DivIcon({
  className: 'leaflet-div-icon-transparent',
  html: `<div style="position:relative;display:inline-block;">
    <div style="width:34px;height:34px;border-radius:50%;background:#92400e;border:3px solid white;color:white;display:flex;align-items:center;justify-content:center;font-size:18px;box-shadow:0 2px 8px rgba(0,0,0,0.4);">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
    </div>
    <div style="position:absolute;bottom:-8px;left:50%;transform:translateX(-50%);width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:8px solid #92400e;"></div>
  </div>`,
  iconSize: null,
  iconAnchor: [17, 42]
});

// Map click handler for markers and pogon drawing only
const MapClickHandler = ({ onMapClick, isActive }) => {
  useMapEvents({
    click: (e) => {
      if (isActive) {
        onMapClick(e.latlng);
      }
    },
  });
  return null;
};

// ZGS GetFeatureInfo click handler – when hunting-boundary layer is on,
// clicking anywhere returns lovisce info from WMS GetFeatureInfo endpoint.
const ZgsFeatureInfoHandler = ({ enabled }) => {
  const map = useMap();
  useMapEvents({
    click: async (e) => {
      if (!enabled) return;
      try {
        const point = map.latLngToContainerPoint(e.latlng);
        const size = map.getSize();
        const bounds = map.getBounds();
        const sw = L.CRS.EPSG3857.project(bounds.getSouthWest());
        const ne = L.CRS.EPSG3857.project(bounds.getNorthEast());
        const bbox = `${sw.x},${sw.y},${ne.x},${ne.y}`;
        const params = new URLSearchParams({
          SERVICE: "WMS",
          VERSION: "1.1.1",
          REQUEST: "GetFeatureInfo",
          LAYERS: "pregledovalnik:lovisca",
          QUERY_LAYERS: "pregledovalnik:lovisca",
          SRS: "EPSG:3857",
          BBOX: bbox,
          WIDTH: String(Math.round(size.x)),
          HEIGHT: String(Math.round(size.y)),
          X: String(Math.round(point.x)),
          Y: String(Math.round(point.y)),
          INFO_FORMAT: "application/json",
          FEATURE_COUNT: "5",
        });
        const url = `https://prostor.zgs.gov.si/geoserver/wms?${params.toString()}`;
        const resp = await fetch(url);
        if (!resp.ok) return;
        const data = await resp.json();
        const feat = (data?.features || [])[0];
        if (!feat) return;
        const p = feat.properties || {};
        const html = `
          <div style="min-width:180px">
            <div style="font-weight:600;color:#1e3a1e;font-size:13px;margin-bottom:4px">
              ${p.lov_naziv || "Lovišče"}
            </div>
            ${p.luo_naziv ? `<div style="font-size:11px;color:#555">Območje: <b>${p.luo_naziv}</b></div>` : ""}
            ${p.lov_povrsina ? `<div style="font-size:11px;color:#555">Površina: ${p.lov_povrsina} ha</div>` : ""}
            <div style="font-size:9px;color:#999;margin-top:4px">Vir: ZGS</div>
          </div>`;
        L.popup({ maxWidth: 260 })
          .setLatLng(e.latlng)
          .setContent(html)
          .openOn(map);
      } catch (_) { /* ignore */ }
    },
  });
  return null;
};

// Long press + drag handler for stojišča and gonjači
const LongPressHandler = ({ isPlacingStojisce, isPlacingGonjac, onPlaceStojisce, onGonjacComplete, gonjacPreviewRef }) => {
  const map = useMap();
  const timerRef = useRef(null);
  const startScreenRef = useRef(null);
  const longPressedRef = useRef(false);
  const gonjacStartRef = useRef(null);

  const callbacksRef = useRef({ onPlaceStojisce, onGonjacComplete });
  callbacksRef.current = { onPlaceStojisce, onGonjacComplete };

  useEffect(() => {
    if (!isPlacingStojisce && !isPlacingGonjac) return;
    const container = map.getContainer();

    const screenPos = (e) => {
      const t = e.touches?.[0] || e.changedTouches?.[0] || e;
      return { x: t.clientX, y: t.clientY };
    };
    const toLatLng = (screenX, screenY) => {
      const rect = container.getBoundingClientRect();
      return map.containerPointToLatLng(L.point(screenX - rect.left, screenY - rect.top));
    };

    const onDown = (e) => {
      const el = e.target;
      if (el.closest && (el.closest('.leaflet-control-container') || el.closest('.leaflet-popup'))) return;
      const pos = screenPos(e);
      startScreenRef.current = pos;
      longPressedRef.current = false;
      gonjacStartRef.current = null;

      timerRef.current = setTimeout(() => {
        longPressedRef.current = true;
        const ll = toLatLng(startScreenRef.current.x, startScreenRef.current.y);
        if (isPlacingStojisce) {
          callbacksRef.current.onPlaceStojisce({ lat: ll.lat, lng: ll.lng });
        } else if (isPlacingGonjac) {
          gonjacStartRef.current = { lat: ll.lat, lng: ll.lng };
          gonjacPreviewRef.current = { lat: ll.lat, lng: ll.lng, dirLat: ll.lat, dirLng: ll.lng };
          map.dragging.disable();
        }
      }, 400);
    };

    const onMove = (e) => {
      if (!longPressedRef.current && timerRef.current && startScreenRef.current) {
        const pos = screenPos(e);
        if (Math.abs(pos.x - startScreenRef.current.x) > 8 || Math.abs(pos.y - startScreenRef.current.y) > 8) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      }
      if (longPressedRef.current && isPlacingGonjac && gonjacStartRef.current) {
        const pos = screenPos(e);
        const ll = toLatLng(pos.x, pos.y);
        gonjacPreviewRef.current = { ...gonjacStartRef.current, dirLat: ll.lat, dirLng: ll.lng };
      }
    };

    const onUp = (e) => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      if (longPressedRef.current && isPlacingGonjac && gonjacStartRef.current) {
        map.dragging.enable();
        const pos = screenPos(e);
        const ll = toLatLng(pos.x, pos.y);
        const dx = Math.abs(ll.lat - gonjacStartRef.current.lat);
        const dy = Math.abs(ll.lng - gonjacStartRef.current.lng);
        if (dx > 0.0001 || dy > 0.0001) {
          callbacksRef.current.onGonjacComplete({ ...gonjacStartRef.current, dirLat: ll.lat, dirLng: ll.lng });
        }
        gonjacPreviewRef.current = null;
      }
      longPressedRef.current = false;
      gonjacStartRef.current = null;
    };

    container.addEventListener('mousedown', onDown);
    container.addEventListener('mousemove', onMove);
    container.addEventListener('mouseup', onUp);
    container.addEventListener('touchstart', onDown, { passive: true });
    container.addEventListener('touchmove', onMove, { passive: true });
    container.addEventListener('touchend', onUp);

    return () => {
      container.removeEventListener('mousedown', onDown);
      container.removeEventListener('mousemove', onMove);
      container.removeEventListener('mouseup', onUp);
      container.removeEventListener('touchstart', onDown);
      container.removeEventListener('touchmove', onMove);
      container.removeEventListener('touchend', onUp);
      if (timerRef.current) clearTimeout(timerRef.current);
      map.dragging.enable();
    };
  }, [map, isPlacingStojisce, isPlacingGonjac, gonjacPreviewRef]);

  return null;
};

// Renders gonjač drag preview as an arrow on the map - re-renders via animation frame
const GonjacDragPreview = ({ gonjacPreviewRef }) => {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    let raf;
    const tick = () => {
      setPreview(gonjacPreviewRef.current ? { ...gonjacPreviewRef.current } : null);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [gonjacPreviewRef]);

  if (!preview) return null;
  const angle = Math.atan2(preview.dirLng - preview.lng, preview.dirLat - preview.lat);
  const dist = Math.sqrt(Math.pow(preview.dirLat - preview.lat, 2) + Math.pow(preview.dirLng - preview.lng, 2));
  const arrowLen = Math.max(dist, 0.0005);
  const tipLat = preview.lat + arrowLen * Math.cos(angle);
  const tipLng = preview.lng + arrowLen * Math.sin(angle);
  const ha = 25 * Math.PI / 180;
  return (
    <>
      <Marker
        position={[preview.lat, preview.lng]}
        icon={L.divIcon({
          className: '',
          html: `<div style="width:28px;height:28px;border-radius:50%;background:#16a34a;border:3px solid white;opacity:0.7;color:white;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:12px;box-shadow:0 2px 8px rgba(0,0,0,0.3);transform:translate(-50%,-50%);">G</div>`,
          iconSize: [0, 0]
        })}
        interactive={false}
      />
      <Polyline
        positions={[[preview.lat, preview.lng], [tipLat, tipLng]]}
        pathOptions={{ color: '#16a34a', weight: 3, opacity: 0.6, dashArray: '6 4' }}
      />
      <Polygon
        positions={[
          [tipLat, tipLng],
          [tipLat - 0.0006 * Math.cos(angle - ha), tipLng - 0.0006 * Math.sin(angle - ha)],
          [tipLat - 0.0006 * Math.cos(angle + ha), tipLng - 0.0006 * Math.sin(angle + ha)]
        ]}
        pathOptions={{ color: '#16a34a', fillColor: '#16a34a', fillOpacity: 0.6, weight: 1 }}
      />
    </>
  );
};

// Component to center map on location
const CenterMapOnLocation = ({ location, shouldCenter, onCentered }) => {
  const map = useMap();
  
  useEffect(() => {
    if (shouldCenter && location) {
      map.flyTo([location.lat, location.lng], 15, { duration: 1 });
      onCentered();
    }
  }, [shouldCenter, location, map, onCentered]);
  
  return null;
};

const HuntingMapPage = () => {
  const { token, hasRole, user } = useAuth();
  const routerLocation = useLocation();
  const showImport = new URLSearchParams(routerLocation.search).get("uvoz") === "1";
  const [mobileControlsOpen, setMobileControlsOpen] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [boundaries, setBoundaries] = useState([]);
  const [pogonLines, setPogonLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isAddingMarker, setIsAddingMarker] = useState(false);
  const [isDrawingPogon, setIsDrawingPogon] = useState(false);
  const [currentPogonPoints, setCurrentPogonPoints] = useState([]);
  const [newMarker, setNewMarker] = useState({ lat: 0, lng: 0, naziv: "", tip: "lokalno_ime", opis: "" });
  const [userLocation, setUserLocation] = useState(null);
  const [heading, setHeading] = useState(null);

  // Listen to device orientation (compass) if available – provides where user is FACING
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e) => {
      // Prefer absolute heading (compass) if provided; else use webkit iOS-specific field
      let h = null;
      if (typeof e.webkitCompassHeading === "number") {
        h = e.webkitCompassHeading; // iOS: already 0-360 clockwise from N
      } else if (typeof e.alpha === "number" && e.absolute) {
        h = 360 - e.alpha; // Android absolute: alpha is CCW; invert to compass
      } else if (typeof e.alpha === "number") {
        h = 360 - e.alpha; // best-effort fallback (may drift)
      }
      if (h != null && !isNaN(h)) {
        setHeading(((h % 360) + 360) % 360);
      }
    };
    // iOS requires explicit permission via user gesture; try silently first
    const tryStart = async () => {
      try {
        if (typeof DeviceOrientationEvent !== "undefined" &&
            typeof DeviceOrientationEvent.requestPermission === "function") {
          const perm = await DeviceOrientationEvent.requestPermission();
          if (perm !== "granted") return;
        }
      } catch { /* not iOS or already granted */ }
      window.addEventListener("deviceorientationabsolute", handler, true);
      window.addEventListener("deviceorientation", handler, true);
    };
    tryStart();
    return () => {
      window.removeEventListener("deviceorientationabsolute", handler, true);
      window.removeEventListener("deviceorientation", handler, true);
    };
  }, []);
  const [shouldCenterOnUser, setShouldCenterOnUser] = useState(false);
  const [isSharingLocation, setIsSharingLocation] = useState(false);
  const [otherMembersLocations, setOtherMembersLocations] = useState([]);
  const MAP_LAYERS_KEY = "hunting_map_layers_v1";
  const [layerPickerOpen, setLayerPickerOpen] = useState(() => typeof window === "undefined" || window.innerWidth >= 768);
  const [scentPhase, setScentPhase] = useState(0);
  const [showLayers, setShowLayers] = useState(() => {
    const defaults = {
      // Legacy overlays — turned OFF by default (moved into new floating picker or removed entirely)
      lokalnaImena: false,
      kmKamni: false,
      meje: false,
      pogoni: false,
      satelit: true,       // Satellite base ON to match reference design
      relief: false,
      mojaLokacija: true,
      padavine: false,
      veter: false,
      stozecVonja: true,   // Scent blob – always visible when user location known
      razdalje: false,
      vremeWidget: false,  // Weather bubble removed from map per user request
      zgsLovisca: true,    // Hunting boundaries ON by default (main context)
      zgsLuo: false,
      zgsRezimi: false,
      visokePreze: true,
    };
    try {
      const raw = localStorage.getItem(MAP_LAYERS_KEY);
      if (raw) {
        // Merge saved with new defaults but FORCE certain layers off (user requested cleanup)
        const merged = { ...defaults, ...JSON.parse(raw) };
        merged.vremeWidget = false;
        merged.pogoni = false;
        merged.lokalnaImena = false;
        merged.kmKamni = false;
        merged.meje = false;
        merged.veter = false;
        merged.razdalje = false;
        merged.mojaLokacija = true;
        return merged;
      }
    } catch { /* ignore */ }
    return defaults;
  });

  useEffect(() => {
    try {
      localStorage.setItem(MAP_LAYERS_KEY, JSON.stringify(showLayers));
    } catch { /* ignore */ }
  }, [showLayers]);

  // Animate smoke phase when scent layer is visible
  useEffect(() => {
    if (!showLayers?.stozecVonja) return;
    const id = setInterval(() => setScentPhase((p) => (p + 0.08) % (Math.PI * 20)), 120);
    return () => clearInterval(id);
  }, [showLayers?.stozecVonja]);
  
  // Weather state
  const [weather, setWeather] = useState(null);
  const [radarFrames, setRadarFrames] = useState([]); // [{ path, time }]
  const [radarIndex, setRadarIndex] = useState(0);
  const [radarPlaying, setRadarPlaying] = useState(true);
  
  // Hunt planning state
  const [isPlacingStojisce, setIsPlacingStojisce] = useState(false);
  const [isPlacingGonjac, setIsPlacingGonjac] = useState(false);
  const [stojisc, setStojisc] = useState([]);
  const [gonjaci, setGonjaci] = useState([]);
  const gonjacPreviewRef = useRef(null);
  
  // Saved drive plans from DB
  const [savedPlans, setSavedPlans] = useState([]);
  const [activePlanId, setActivePlanId] = useState(null);
  const [savePlanDialogOpen, setSavePlanDialogOpen] = useState(false);
  const [planName, setPlanName] = useState("");
  
  // Member hunt role (stojišče = red, gonjač = green)
  const [myHuntRole, setMyHuntRole] = useState("stojisce"); // "stojisce" | "gonjac"

  // High seats refresh key
  const [highSeatsRefresh, setHighSeatsRefresh] = useState(0);
  
  const canEdit = hasRole(["administrator", "predsednik", "gospodar", "tajnik"]) || (user?.permissions || []).includes("urejanje_karte");

  // Center on Rakek
  const mapCenter = [45.7833, 14.2167];
  const mapZoom = 13;

  // Neighbor hunting grounds colors - with short names
  const neighborColors = {
    "RAKEK": { color: "#16a34a", label: "LD RAKEK" },
    "BEGUNJE": { color: "#dc2626", label: "LD BEGUNJE" },
    "JAVORNIK-POSTOJNA": { color: "#db2777", label: "LD JAVORNIK POSTOJNA" },
    "CERKNICA": { color: "#0891b2", label: "LD CERKNICA" },
    "LJUBLJANSKI VRH": { color: "#2563eb", label: "LPN LJUBLJANSKI VRH" },
    "PLANINA": { color: "#7c3aed", label: "LD PLANINA" },
    "LOGATEC": { color: "#d97706", label: "LD LOGATEC" }
  };

  useEffect(() => {
    fetchMapData();
    fetchDrivePlans();
    loadKMLBoundaries();
    getUserLocation();
    fetchWeather();               // initial fetch with LD default coords
    fetchRadarTimestamp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch weather when the user's real location arrives (so vonj blob is centered on user).
  useEffect(() => {
    if (userLocation?.lat && userLocation?.lng) {
      fetchWeather(userLocation.lat, userLocation.lng);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation?.lat, userLocation?.lng]);

  // Auto-refresh weather + radar every 15 min (Open-Meteo current-weather updates hourly;
  // 15 min polling with backend TTL cache limits upstream hits to ~4/h max).
  useEffect(() => {
    const interval = setInterval(() => {
      const lat = userLocation?.lat;
      const lng = userLocation?.lng;
      fetchWeather(lat, lng);
      fetchRadarTimestamp();
    }, 15 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation?.lat, userLocation?.lng]);

  // Radar animation loop – cycles through frames every ~600 ms when playing
  useEffect(() => {
    if (!showLayers?.padavine || !radarPlaying || radarFrames.length < 2) return;
    const id = setInterval(() => {
      setRadarIndex((i) => (i + 1) % radarFrames.length);
    }, 600);
    return () => clearInterval(id);
  }, [showLayers?.padavine, radarPlaying, radarFrames.length]);

  // Get user's current location
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.watchPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setUserLocation(newLocation);
          
          // If sharing is enabled, update server
          if (isSharingLocation) {
            shareLocationToServer(newLocation);
          }
        },
        (error) => {
          // Geolocation unavailable
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
      );
    }
  };

  // Share location to server
  const shareLocationToServer = async (location) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/location/share`, { ...location, hunt_role: myHuntRole }, { headers });
    } catch (error) {
      console.error("Error sharing location:", error);
    }
  };

  // Stop sharing location
  const stopSharingLocation = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/location/stop`, {}, { headers });
    } catch (error) {
      console.error("Error stopping location share:", error);
    }
  };

  // Fetch other members' locations
  const fetchOtherLocations = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/location/others`, { headers });
      setOtherMembersLocations(response.data || []);
    } catch (error) {
      console.error("Error fetching other locations:", error);
    }
  };

  // Toggle location sharing
  const toggleLocationSharing = () => {
    if (isSharingLocation) {
      stopSharingLocation();
      setIsSharingLocation(false);
      toast.info("Deljenje lokacije ustavljeno");
    } else {
      if (userLocation) {
        shareLocationToServer(userLocation);
      }
      setIsSharingLocation(true);
      toast.success("Deljenje lokacije vklopljeno");
    }
  };

  // Re-share when role changes
  useEffect(() => {
    if (isSharingLocation && userLocation) {
      shareLocationToServer(userLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myHuntRole]);

  // Fetch weather data. If lat/lon are provided, use them (accurate for user location);
  // otherwise backend falls back to LD center coordinates.
  const fetchWeather = async (lat, lon) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = (lat != null && lon != null) ? { lat, lon } : {};
      const res = await axios.get(`${API}/weather`, { headers, params });
      setWeather(res.data);
    } catch (e) {
      // Weather unavailable
    }
  };

  // Fetch RainViewer radar frames (past + nowcast) for animated loop
  const fetchRadarTimestamp = async () => {
    try {
      const res = await axios.get("https://api.rainviewer.com/public/weather-maps.json");
      const past = res.data?.radar?.past || [];
      const nowcast = res.data?.radar?.nowcast || [];
      const frames = [...past, ...nowcast].map(f => ({ path: f.path, time: f.time }));
      if (frames.length > 0) {
        setRadarFrames(frames);
        // Jump to most recent past frame (end of past = latest observed)
        const lastObservedIdx = Math.max(0, past.length - 1);
        setRadarIndex(lastObservedIdx);
      }
    } catch (e) {
      // RainViewer unavailable
    }
  };

  // Seeded pseudo-random for consistent shapes
  const srand = (n) => {
    const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
    return x - Math.floor(x);
  };

  // Generate scent dispersal — BILLOWING SMOKE/FOG STYLE
  // Multiple overlapping irregular "puffs" positioned along the wind axis, each with
  // organic shape (radial noise) and pulsing size/offset for a rolling / valujoč effect.
  // 1km ≈ 0.009 degrees latitude
  const generateScentCone = (lat, lng, windDirDeg, windSpeed, phase = 0) => {
    // Downwind direction (where scent goes)
    const dirRad = ((windDirDeg + 180) % 360) * Math.PI / 180;
    const perpRad = dirRad + Math.PI / 2; // perpendicular for lateral drift
    const cosLat = Math.cos(lat * Math.PI / 180);
    const seed = Math.floor(windDirDeg * 137 + windSpeed * 43);

    // Length + spread scale with wind
    const coneLen = windSpeed > 15 ? 0.016 : windSpeed > 8 ? 0.012 : 0.010;

    // Number of overlapping puffs along the axis (denser = smoother billowing)
    const numPuffs = 14;
    const layers = [];

    for (let p = 0; p < numPuffs; p++) {
      const frac = (p + 1) / numPuffs; // 0→1 from source to tail
      // Distance along wind axis (with small stagger jitter)
      const jitterFwd = (srand(seed + p * 17) - 0.5) * 0.05;
      // Offset all puffs 8% downwind so user pin remains visible at source
      const dist = coneLen * (0.08 + frac * 0.92 + jitterFwd);
      // Perpendicular sideways offset – oscillates in time so the plume "waves"
      const wavePhase = phase + p * 0.7;
      const sideOsc = Math.sin(wavePhase) * 0.35 + Math.sin(wavePhase * 1.7) * 0.15;
      const sideBase = (srand(seed + p * 41) - 0.5) * 0.4;
      const side = (sideBase + sideOsc) * coneLen * 0.22 * frac;
      const cx = lat  + dist * Math.cos(dirRad) + side * Math.cos(perpRad);
      const cy = lng + (dist * Math.sin(dirRad) + side * Math.sin(perpRad)) / cosLat;

      // Puff radius grows with distance – small at source, large at tail.
      // Also pulses in time & has noise so puffs look asymmetric/irregular.
      const baseR = coneLen * (0.05 + 0.28 * frac);
      const pulse = 1 + Math.sin(phase * 1.3 + p) * 0.10;
      const R = baseR * pulse;

      // Build irregular polygon around (cx, cy) with radial noise
      const segs = 40;
      const pts = [];
      for (let i = 0; i < segs; i++) {
        const a = (i / segs) * Math.PI * 2;
        // Multi-frequency noise → organic edge (rolling smoke)
        const n1 = (srand(seed + p * 100 + i) - 0.5) * 0.55;
        const n2 = Math.sin(a * 3 + phase * 1.5 + p) * 0.20;
        const n3 = Math.sin(a * 7 + phase * 0.7) * 0.12;
        const rNoisy = R * (1 + n1 + n2 + n3);
        // Elongate in wind direction slightly so puff is oval, not round
        const ex = rNoisy * (1.15 + 0.35 * Math.cos(a - dirRad));
        pts.push([
          cx + ex * Math.cos(a),
          cy + ex * Math.sin(a) / cosLat,
        ]);
      }

      // Color: intense orange near source → pale warm at tail
      const r = Math.round(210 + 45 * frac);
      const g = Math.round(70  + 130 * frac);
      const b = Math.round(15  + 65 * frac);
      // Opacity: strongest 2nd-3rd puff (visible core), fading at head/tail
      const centralWeight = 1 - Math.abs(frac - 0.35) * 1.4;
      const opacity = Math.max(0.06, 0.42 * Math.max(0.15, centralWeight));

      layers.push({ points: pts, color: `rgb(${r},${g},${b})`, opacity });
    }

    return layers;
  };

  // Generate distance rings
  const generateDistanceRings = (lat, lng) => {
    return [100, 200, 300, 400].map(dist => ({
      distance: dist,
      radius: dist
    }));
  };

  // Fetch other members' locations periodically when sharing is active
  useEffect(() => {
    let interval;
    if (isSharingLocation) {
      fetchOtherLocations();
      interval = setInterval(fetchOtherLocations, 15000); // Every 15 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSharingLocation]);

  const fetchMapData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/map/markers`, { headers });
      setMarkers(response.data.markers || []);
      setPogonLines(response.data.pogon_lines || []);
    } catch (error) {
      console.error("Error fetching map data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch saved drive plans
  const fetchDrivePlans = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/pogoni/nacrti`, { headers });
      const nacrti = response.data.nacrti || [];
      setSavedPlans(nacrti);
      // Auto-load plan from push notification URL (?plan=<id>)
      const planParam = new URLSearchParams(window.location.search).get("plan");
      if (planParam && !activePlanId) {
        const p = nacrti.find(n => n.id === planParam);
        if (p) {
          setActivePlanId(p.id);
          setStojisc(p.stojisca || []);
          setGonjaci(p.gonjaci || []);
          setShowLayers(prev => ({ ...prev, pogoni: true }));
          toast.info(`Naložen načrt pogona: ${p.naziv}`);
        }
      }
    } catch (error) {
      console.error("Error fetching drive plans:", error);
    }
  };

  // Save current drive plan to DB
  const handleSaveDrivePlan = async () => {
    if (stojisc.length === 0 && gonjaci.length === 0) {
      toast.error("Najprej dodajte stojišča ali gonjače");
      return;
    }
    if (!planName.trim()) {
      toast.error("Vnesite naziv pogona");
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/pogoni/nacrt`, {
        naziv: planName.trim(),
        stojisca: stojisc,
        gonjaci: gonjaci
      }, { headers });
      toast.success("Načrt pogona shranjen");
      setSavePlanDialogOpen(false);
      setPlanName("");
      setStojisc([]);
      setGonjaci([]);
      setIsPlacingStojisce(false);
      setIsPlacingGonjac(false);
      fetchDrivePlans();
    } catch (error) {
      toast.error("Napaka pri shranjevanju načrta");
    }
  };

  // Update the currently loaded (active) plan with current stojišča/gonjači
  const handleUpdateDrivePlan = async () => {
    if (!activePlanId) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/pogoni/nacrt/${activePlanId}`, {
        stojisca: stojisc,
        gonjaci: gonjaci
      }, { headers });
      toast.success("Spremembe načrta shranjene");
      fetchDrivePlans();
    } catch (error) {
      toast.error("Napaka pri posodabljanju načrta");
    }
  };

  // Delete a saved drive plan
  const handleDeleteDrivePlan = async (planId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/pogoni/nacrt/${planId}`, { headers });
      toast.success("Načrt pogona izbrisan");
      if (activePlanId === planId) setActivePlanId(null);
      fetchDrivePlans();
    } catch (error) {
      toast.error("Napaka pri brisanju");
    }
  };

  // Load a saved plan to show on map
  // Share plan with members via push
  const handleSharePlan = async (plan) => {
    if (!window.confirm(`Pošljem push obvestilo z načrtom »${plan.naziv}« vsem članom LD?`)) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/pogoni/nacrt/${plan.id}/share`, {}, { headers });
      toast.success(`Načrt poslan (${res.data.members} članov)`);
    } catch (error) {
      toast.error("Napaka pri pošiljanju načrta");
    }
  };

  const planShareUrl = (planId) => `${window.location.origin}/karta?plan=${planId}`;
  const [qrPlan, setQrPlan] = useState(null);

  const sharePlanSms = (plan) => {
    const text = `Načrt pogona »${plan.naziv}« – odpri v Dober pogled app: ${planShareUrl(plan.id)}`;
    window.location.href = `sms:?&body=${encodeURIComponent(text)}`;
  };

  const sharePlanCopy = async (plan) => {
    try {
      await navigator.clipboard.writeText(planShareUrl(plan.id));
      toast.success("Povezava kopirana");
    } catch {
      toast.error("Kopiranje ni uspelo");
    }
  };

  const sharePlanNative = async (plan) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Načrt pogona: ${plan.naziv}`,
          text: `Načrt pogona »${plan.naziv}« – Dober pogled app`,
          url: planShareUrl(plan.id),
        });
      } catch { /* preklicano */ }
    } else {
      sharePlanCopy(plan);
    }
  };

  const handleLoadPlan = (plan) => {
    if (activePlanId === plan.id) {
      setActivePlanId(null);
      setStojisc([]);
      setGonjaci([]);
    } else {
      setActivePlanId(plan.id);
      setStojisc(plan.stojisca || []);
      setGonjaci(plan.gonjaci || []);
      setShowLayers(prev => ({ ...prev, pogoni: true }));
    }
  };

  const loadKMLBoundaries = async () => {
    try {
      // Load the KML file
      const response = await fetch("/data/lovisce.kml");
      const kmlText = await response.text();
      
      // Parse KML
      const parser = new DOMParser();
      const kml = parser.parseFromString(kmlText, "text/xml");
      const placemarks = kml.getElementsByTagName("Placemark");
      
      const parsedBoundaries = [];
      
  // Only show LD RAKEK boundary
  const targetHuntingGrounds = ["RAKEK"];
      
      for (let i = 0; i < placemarks.length; i++) {
        const placemark = placemarks[i];
        
        // Get hunting ground name from SimpleData
        const simpleDataNodes = placemark.getElementsByTagName("SimpleData");
        let lovNaziv = "";
        for (let j = 0; j < simpleDataNodes.length; j++) {
          if (simpleDataNodes[j].getAttribute("name") === "lov_naziv") {
            lovNaziv = simpleDataNodes[j].textContent.trim().toUpperCase();
            break;
          }
        }
        
        // Only include our target hunting grounds
        if (!targetHuntingGrounds.includes(lovNaziv)) continue;
        
        const coordsEl = placemark.getElementsByTagName("coordinates")[0];
        if (coordsEl) {
          const coordsText = coordsEl.textContent.trim();
          const coords = coordsText.split(/\s+/).map(coord => {
            const [lng, lat] = coord.split(",").map(parseFloat);
            return [lat, lng];
          }).filter(c => !isNaN(c[0]) && !isNaN(c[1]));
          
          if (coords.length > 2) {
            const colorInfo = neighborColors[lovNaziv] || { color: "#6b7280", label: lovNaziv };
            parsedBoundaries.push({
              name: lovNaziv,
              label: colorInfo.label,
              coordinates: coords,
              color: colorInfo.color
            });
          }
        }
      }
      
      setBoundaries(parsedBoundaries);
    } catch (error) {
      console.error("Error loading KML:", error);
    }
  };

  const handleMapClick = (latlng) => {
    if (isDrawingPogon) {
      setCurrentPogonPoints([...currentPogonPoints, [latlng.lat, latlng.lng]]);
    } else if (isAddingMarker) {
      setNewMarker({ ...newMarker, lat: latlng.lat, lng: latlng.lng });
      setDialogOpen(true);
      setIsAddingMarker(false);
    }
  };

  // Long press callbacks for stojišča/gonjači
  const handlePlaceStojisce = (latlng) => {
    const num = stojisc.length + 1;
    setStojisc(prev => [...prev, { lat: latlng.lat, lng: latlng.lng, num }]);
    toast.success(`Stojišče ${num} dodano`);
  };

  const handleGonjacComplete = (data) => {
    setGonjaci(prev => [...prev, data]);
    toast.success(`Gonjač ${gonjaci.length + 1} dodan`);
  };

  const handleSaveMarker = async () => {
    if (!newMarker.naziv.trim()) {
      toast.error("Vnesite naziv");
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/map/markers`, newMarker, { headers });
      toast.success("Oznaka dodana");
      setDialogOpen(false);
      setNewMarker({ lat: 0, lng: 0, naziv: "", tip: "lokalno_ime", opis: "" });
      fetchMapData();
    } catch (error) {
      toast.error("Napaka pri shranjevanju");
    }
  };

  const handleDeleteMarker = async (markerId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/map/markers/${markerId}`, { headers });
      toast.success("Oznaka izbrisana");
      fetchMapData();
    } catch (error) {
      toast.error("Napaka pri brisanju");
    }
  };

  const handleSavePogon = async () => {
    if (currentPogonPoints.length < 2) {
      toast.error("Potrebni sta vsaj 2 točki");
      return;
    }
    
    const naziv = prompt("Vnesite naziv pogona/smeri:");
    if (!naziv) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/map/pogon`, { naziv, points: currentPogonPoints }, { headers });
      toast.success("Pogon shranjen");
      setCurrentPogonPoints([]);
      setIsDrawingPogon(false);
      fetchMapData();
    } catch (error) {
      toast.error("Napaka pri shranjevanju");
    }
  };

  const handleDeletePogon = async (pogonId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/map/pogon/${pogonId}`, { headers });
      toast.success("Pogon izbrisan");
      fetchMapData();
    } catch (error) {
      toast.error("Napaka pri brisanju");
    }
  };

  // Import KML/KMZ file
  const handleImportKML = async (event, importType) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      let kmlText = "";
      
      // Check if it's a KMZ file (ZIP archive)
      if (file.name.toLowerCase().endsWith('.kmz')) {
        // KMZ is a ZIP file - need to extract the KML inside
        const JSZip = (await import('jszip')).default;
        const zip = await JSZip.loadAsync(file);
        
        // Find the KML file inside (usually doc.kml or *.kml)
        let kmlFile = null;
        for (const [filename, zipEntry] of Object.entries(zip.files)) {
          if (filename.toLowerCase().endsWith('.kml')) {
            kmlFile = zipEntry;
            break;
          }
        }
        
        if (!kmlFile) {
          toast.error("KMZ datoteka ne vsebuje KML");
          return;
        }
        
        kmlText = await kmlFile.async('string');
      } else {
        // Regular KML file
        kmlText = await file.text();
      }
      
      const parser = new DOMParser();
      const kml = parser.parseFromString(kmlText, "text/xml");
      
      if (importType === "km_kamen") {
        // Import kilometer stones as markers
        const placemarks = kml.getElementsByTagName("Placemark");
        let imported = 0;
        
        for (let i = 0; i < placemarks.length; i++) {
          const placemark = placemarks[i];
          const nameEl = placemark.getElementsByTagName("name")[0];
          const coordsEl = placemark.getElementsByTagName("coordinates")[0];
          
          if (coordsEl) {
            const coords = coordsEl.textContent.trim().split(",");
            const lng = parseFloat(coords[0]);
            const lat = parseFloat(coords[1]);
            
            if (!isNaN(lat) && !isNaN(lng)) {
              const naziv = nameEl?.textContent || `Km ${imported + 1}`;
              
              const headers = { Authorization: `Bearer ${token}` };
              await axios.post(`${API}/map/markers`, {
                lat, lng, naziv, tip: "km_kamen", opis: ""
              }, { headers });
              imported++;
            }
          }
        }
        
        toast.success(`Uvoženih ${imported} km kamnov`);
        fetchMapData();
      } else if (importType === "pogon") {
        // Import lines as pogon
        const placemarks = kml.getElementsByTagName("Placemark");
        let imported = 0;
        
        for (let i = 0; i < placemarks.length; i++) {
          const placemark = placemarks[i];
          const nameEl = placemark.getElementsByTagName("name")[0];
          const coordsEl = placemark.getElementsByTagName("coordinates")[0];
          
          if (coordsEl) {
            const coordsText = coordsEl.textContent.trim();
            const points = coordsText.split(/\s+/).map(coord => {
              const [lng, lat] = coord.split(",").map(parseFloat);
              return [lat, lng];
            }).filter(c => !isNaN(c[0]) && !isNaN(c[1]));
            
            if (points.length >= 2) {
              const naziv = nameEl?.textContent || `Pogon ${imported + 1}`;
              
              const headers = { Authorization: `Bearer ${token}` };
              await axios.post(`${API}/map/pogon`, { naziv, points }, { headers });
              imported++;
            }
          }
        }
        
        toast.success(`Uvoženih ${imported} pogonov`);
        fetchMapData();
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Napaka pri uvozu datoteke. Preverite format.");
    }
    
    // Reset input
    event.target.value = "";
  };

  const getMarkerIcon = (marker) => {
    if (marker.tip === "km_kamen") return createKmIcon(marker.naziv);
    if (marker.tip === "lokalno_ime") return createLabelIcon(marker.naziv);
    if (marker.tip === "opazovalnica") return createOpazovalnicaIcon();
    if (marker.tip === "krmisce") return createKrmisceIcon();
    if (marker.tip === "lovska_koca") return createLovskaKocaIcon();
    return icons[marker.tip] || icons.default;
  };

  const filteredMarkers = markers.filter(m => {
    if (m.tip === "lokalno_ime" && !showLayers.lokalnaImena) return false;
    if (m.tip === "km_kamen" && !showLayers.kmKamni) return false;
    return true;
  });

  // Custom icon for user location — small solid dot with optional heading cone,
  // 100% opaque, no shadow, always on top
  const userLocationIcon = new L.DivIcon({
    className: "user-location-dot",
    html: (heading != null
      ? `<div style="position:relative;width:44px;height:44px;">
           <div style="position:absolute;left:50%;top:50%;width:44px;height:44px;margin-left:-22px;margin-top:-22px;transform:rotate(${heading}deg);transform-origin:50% 50%;">
             <svg viewBox="-22 -22 44 44" width="44" height="44" style="overflow:visible;">
               <path d="M -10 -6 L 0 -22 L 10 -6 Z" fill="#dc2626" fill-opacity="0.55" stroke="#ffffff" stroke-width="1"/>
             </svg>
           </div>
           <div style="position:absolute;left:50%;top:50%;width:16px;height:16px;margin-left:-8px;margin-top:-8px;background:#dc2626;border:3px solid #ffffff;border-radius:50%;box-sizing:border-box;"></div>
         </div>`
      : `<div style="width:16px;height:16px;background:#dc2626;border:3px solid #ffffff;border-radius:50%;opacity:1;box-sizing:border-box;"></div>`),
    iconSize: heading != null ? [44, 44] : [16, 16],
    iconAnchor: heading != null ? [22, 22] : [8, 8],
    popupAnchor: [0, -8],
  });

  return (
    <div className="space-y-4 animate-fade-in" data-testid="hunting-map-page">
      <div className="hidden md:flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapIcon className="h-6 w-6 text-green-600" />
            Karta lovišča
          </h1>
          <p className="text-muted-foreground">{user?.ld_naziv ? String(user.ld_naziv).toUpperCase() : "LOVSKA DRUŽINA"}</p>
        </div>
        
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant={isAddingMarker ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setIsAddingMarker(!isAddingMarker);
                setIsPlacingStojisce(false);
                setIsPlacingGonjac(false);
                setIsDrawingPogon(false);
              }}
            >
              <MapPin className="h-4 w-4 mr-1" />
              {isAddingMarker ? "Prekliči" : "Dodaj oznako"}
            </Button>
            
            {/* Import buttons - vidni samo prek menija Upravljanje → Uvoz na karto */}
            {showImport && (
            <>
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".kml,.kmz"
                className="hidden"
                onChange={(e) => handleImportKML(e, "km_kamen")}
              />
              <Button variant="outline" size="sm" asChild>
                <span>
                  <Milestone className="h-4 w-4 mr-1" />
                  Uvoz SŽ km
                </span>
              </Button>
            </label>
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".kml,.kmz,.gpx"
                className="hidden"
                onChange={(e) => handleImportKML(e, "pogon")}
              />
              <Button variant="outline" size="sm" asChild>
                <span>
                  <Navigation className="h-4 w-4 mr-1" />
                  Uvoz pogonov
                </span>
              </Button>
            </label>
            </>
            )}
          </div>
        )}
      </div>

      {/* Layer toggles */}
      <Card className={`p-3 ${mobileControlsOpen ? "" : "hidden"} md:block`}>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="font-medium flex items-center gap-1">
            <Layers className="h-4 w-4" />
            Plasti:
          </span>
          <Button
            variant={showLayers.mojaLokacija ? "default" : "outline"}
            size="sm"
            onClick={async () => {
              if (!showLayers.mojaLokacija) {
                // Turning on - center map on user location
                if (userLocation) {
                  setShouldCenterOnUser(true);
                } else {
                  toast.info("Pridobivam lokacijo...");
                }
                // iOS: request device-orientation permission (user gesture required)
                try {
                  if (typeof DeviceOrientationEvent !== "undefined" &&
                      typeof DeviceOrientationEvent.requestPermission === "function") {
                    const perm = await DeviceOrientationEvent.requestPermission();
                    if (perm !== "granted") {
                      toast.info("Za prikaz smeri gledanja omogočite dostop do kompasa v nastavitvah brskalnika.");
                    }
                  }
                } catch { /* ignore */ }
              }
              setShowLayers({ ...showLayers, mojaLokacija: !showLayers.mojaLokacija });
            }}
            data-testid="my-location-btn"
          >
            <MapPin className="h-3 w-3 mr-1" />
            Moja lokacija
          </Button>
          
          {/* Share location button */}
          <Button
            variant={isSharingLocation ? "default" : "outline"}
            size="sm"
            onClick={toggleLocationSharing}
            className={isSharingLocation ? (myHuntRole === "stojisce" ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700") : ""}
            data-testid="share-location-btn"
          >
            <Share2 className="h-3 w-3 mr-1" />
            {isSharingLocation ? "Lokacija ON" : "Deli lokacijo"}
          </Button>
          
          {/* Hunt role toggle */}
          {isSharingLocation && (
            <div className="flex items-center border rounded-md overflow-hidden">
              <button
                className={`px-2 py-1 text-xs font-medium ${myHuntRole === "stojisce" ? "bg-red-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                onClick={() => setMyHuntRole("stojisce")}
                data-testid="role-stojisce-btn"
              >
                Stojišče
              </button>
              <button
                className={`px-2 py-1 text-xs font-medium ${myHuntRole === "gonjac" ? "bg-green-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                onClick={() => setMyHuntRole("gonjac")}
                data-testid="role-gonjac-btn"
              >
                Gonjač
              </button>
            </div>
          )}
          
          {/* Show count of other members sharing */}
          {isSharingLocation && otherMembersLocations.length > 0 && (
            <Badge variant="secondary" className="bg-purple-100 text-purple-800">
              <Users className="h-3 w-3 mr-1" />
              {otherMembersLocations.length} članov
            </Badge>
          )}

          {/* High seats admin (only for leadership) */}
          <HighSeatsAdminPanel
            token={token}
            user={user}
            onImported={() => setHighSeatsRefresh((k) => k + 1)}
          />
        </div>
        
        {/* Hunt planning controls */}
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
            <span className="text-xs font-medium text-muted-foreground">Načrt pogona:</span>
            <Button
              variant={isPlacingStojisce ? "default" : "outline"}
              size="sm"
              onClick={() => { 
                setIsPlacingStojisce(!isPlacingStojisce); 
                setIsPlacingGonjac(false); 
                setIsDrawingPogon(false);
                setIsAddingMarker(false);
                if (!isPlacingStojisce) setShowLayers(prev => ({ ...prev, pogoni: true }));
              }}
              data-testid="place-stojisce-btn"
            >
              <Target className="h-3 w-3 mr-1" />
              {isPlacingStojisce ? "Končaj stojišča" : "Dodaj stojišče"}
            </Button>
            <Button
              variant={isPlacingGonjac ? "default" : "outline"}
              size="sm"
              onClick={() => { 
                setIsPlacingGonjac(!isPlacingGonjac); 
                setIsPlacingStojisce(false);
                setIsDrawingPogon(false);
                setIsAddingMarker(false);
                if (!isPlacingGonjac) setShowLayers(prev => ({ ...prev, pogoni: true }));
              }}
              data-testid="place-gonjac-btn"
            >
              <Navigation className="h-3 w-3 mr-1" />
              {isPlacingGonjac ? "Končaj gonjače" : "Dodaj gonjača"}
            </Button>
            {(stojisc.length > 0 || gonjaci.length > 0) && !activePlanId && (
              <Button
                size="sm"
                onClick={() => setSavePlanDialogOpen(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="save-plan-btn"
              >
                <Save className="h-3 w-3 mr-1" />
                Shrani načrt
              </Button>
            )}
            {activePlanId && (
              <Button
                size="sm"
                onClick={handleUpdateDrivePlan}
                className="bg-green-600 hover:bg-green-700"
                data-testid="update-plan-btn"
              >
                <Save className="h-3 w-3 mr-1" />
                Shrani spremembe
              </Button>
            )}
            {(stojisc.length > 0 || gonjaci.length > 0) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setStojisc([]); setGonjaci([]); setActivePlanId(null); }}
                className="text-red-600 border-red-200"
                data-testid="clear-plan-btn"
              >
                Počisti načrt
              </Button>
            )}
            {stojisc.length > 0 && (
              <Badge variant="secondary" className="text-xs">{stojisc.length} stojišč</Badge>
            )}
            {gonjaci.length > 0 && (
              <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">{gonjaci.length} gonjačev</Badge>
            )}
          </div>
        )}
        
        {/* Saved drive plans - dropdown */}
        {savedPlans.length > 0 && (
          <div className="px-3 pb-2 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Načrt pogona:</span>
            <Select
              value={activePlanId || "none"}
              onValueChange={(v) => {
                if (v === "none") {
                  setActivePlanId(null);
                  setStojisc([]);
                  setGonjaci([]);
                } else {
                  const p = savedPlans.find((x) => x.id === v);
                  if (p) {
                    setActivePlanId(p.id);
                    setStojisc(p.stojisca || []);
                    setGonjaci(p.gonjaci || []);
                    setShowLayers(prev => ({ ...prev, pogoni: true }));
                  }
                }
              }}
            >
              <SelectTrigger className="h-8 w-[220px] text-xs" data-testid="plan-select">
                <SelectValue placeholder="Izberi načrt ..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Brez načrta —</SelectItem>
                {savedPlans.map((plan) => (
                  <SelectItem key={plan.id} value={plan.id} data-testid={`plan-option-${plan.id}`}>
                    {plan.naziv} ({(plan.stojisca || []).length}S / {(plan.gonjaci || []).length}G)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activePlanId && canEdit && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="share-plan-menu">
                      <Share2 className="h-3.5 w-3.5 mr-1" />
                      Deli
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="z-[10001]">
                    <DropdownMenuItem onClick={() => { const p = savedPlans.find(x => x.id === activePlanId); if (p) handleSharePlan(p); }} data-testid="share-plan-push">
                      <Bell className="h-4 w-4 mr-2" /> Push obvestilo članom
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { const p = savedPlans.find(x => x.id === activePlanId); if (p) sharePlanSms(p); }} data-testid="share-plan-sms">
                      <MessageSquare className="h-4 w-4 mr-2" /> SMS
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { const p = savedPlans.find(x => x.id === activePlanId); if (p) setQrPlan(p); }} data-testid="share-plan-qr">
                      <QrCode className="h-4 w-4 mr-2" /> QR koda
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { const p = savedPlans.find(x => x.id === activePlanId); if (p) sharePlanCopy(p); }} data-testid="share-plan-copy">
                      <Copy className="h-4 w-4 mr-2" /> Kopiraj povezavo
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { const p = savedPlans.find(x => x.id === activePlanId); if (p) sharePlanNative(p); }} data-testid="share-plan-native">
                      <Share2 className="h-4 w-4 mr-2" /> Drugo (sistemsko deljenje)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteDrivePlan(activePlanId)}
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                  title="Izbriši načrt"
                  data-testid="delete-active-plan"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </div>
        )}
        
      </Card>

      {/* QR dialog za deljenje načrta */}
      {qrPlan && (
        <Dialog open={true} onOpenChange={(o) => { if (!o) setQrPlan(null); }}>
          <DialogContent className="max-w-sm" data-testid="plan-qr-dialog">
            <DialogHeader>
              <DialogTitle>QR koda načrta: {qrPlan.naziv}</DialogTitle>
              <DialogDescription>Člani naj skenirajo kodo s telefonom — odpre karto z naloženim načrtom.</DialogDescription>
            </DialogHeader>
            <div className="flex justify-center py-4">
              <div className="bg-white p-4 rounded-xl border">
                <QRCodeSVG value={planShareUrl(qrPlan.id)} size={220} />
              </div>
            </div>
            <p className="text-xs text-center text-muted-foreground break-all">{planShareUrl(qrPlan.id)}</p>
          </DialogContent>
        </Dialog>
      )}

      {/* Map */}
      <Card className="overflow-hidden -mx-4 md:mx-0 rounded-none md:rounded-lg border-x-0 md:border-x">
        <div className="h-[calc(100dvh-10.5rem)] md:h-[600px] relative">
          {/* Floating mobile controls (left side) */}
          <div className="md:hidden absolute left-2 top-20 flex flex-col gap-2" style={{ zIndex: 10000 }}>
            <button
              onClick={() => setMobileControlsOpen((o) => !o)}
              className={`w-11 h-11 rounded-full shadow-lg border flex items-center justify-center ${mobileControlsOpen ? "bg-primary text-white" : "bg-white text-slate-700"}`}
              title="Upravljanje karte"
              data-testid="mobile-controls-toggle"
            >
              <SlidersHorizontal className="h-5 w-5" />
            </button>
            <button
              onClick={() => {
                if (!showLayers.mojaLokacija) setShouldCenterOnUser(true);
                setShowLayers(prev => ({ ...prev, mojaLokacija: !prev.mojaLokacija }));
              }}
              className={`w-11 h-11 rounded-full shadow-lg border flex items-center justify-center ${showLayers.mojaLokacija ? "bg-primary text-white" : "bg-white text-slate-700"}`}
              title="Moja lokacija"
              data-testid="mobile-location-toggle"
            >
              <LocateFixed className="h-5 w-5" />
            </button>
            <button
              onClick={toggleLocationSharing}
              className={`w-11 h-11 rounded-full shadow-lg border flex items-center justify-center ${isSharingLocation ? (myHuntRole === "gonjac" ? "bg-green-600" : "bg-red-600") + " text-white" : "bg-white text-slate-700"}`}
              title="Deli lokacijo"
              data-testid="mobile-share-location"
            >
              <Share2 className="h-5 w-5" />
            </button>
          </div>
          {/* Floating base+overlay layer picker (top-right, collapsible) */}
          <div
            className="absolute top-3 right-3"
            style={{ zIndex: 10000 }}
            data-testid="map-layer-picker"
          >
            {layerPickerOpen ? (
              <div className="bg-white rounded-xl shadow-2xl border-2 border-primary/30 p-3 w-[220px] text-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-bold text-slate-900 flex items-center gap-1 text-base">
                    <Layers className="h-4 w-4 text-primary" />
                    Sloji
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        fetchWeather(userLocation?.lat, userLocation?.lng);
                        fetchRadarTimestamp();
                      }}
                      className="text-slate-400 hover:text-primary text-xs px-1.5 py-0.5 rounded hover:bg-slate-100 transition"
                      aria-label="Osveži vreme"
                      title="Osveži vreme in radar"
                      data-testid="refresh-weather-btn"
                    >
                      ↻
                    </button>
                    <button
                      onClick={() => setLayerPickerOpen(false)}
                      className="text-slate-400 hover:text-slate-700 text-lg leading-none"
                      aria-label="Zapri"
                      data-testid="layer-picker-close"
                    >
                      ×
                    </button>
                  </div>
                </div>

                {/* Base layer radio group */}
                <div className="space-y-1.5">
                  {[
                    { key: "osm",     label: "Zemljevid (OSM)" },
                    { key: "satelit", label: "Satelit" },
                    { key: "relief",  label: "Relief (Topo)" },
                  ].map(({ key, label }) => {
                    const selected =
                      (key === "osm"     && !showLayers.satelit && !showLayers.relief) ||
                      (key === "satelit" && showLayers.satelit) ||
                      (key === "relief"  && showLayers.relief);
                    return (
                      <label
                        key={key}
                        className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 rounded px-1 py-0.5"
                        data-testid={`base-layer-${key}`}
                      >
                        <input
                          type="radio"
                          name="baseLayer"
                          checked={selected}
                          onChange={() => setShowLayers({
                            ...showLayers,
                            satelit: key === "satelit",
                            relief:  key === "relief",
                          })}
                          className="accent-primary"
                        />
                        <span className="text-slate-900 font-medium">{label}</span>
                      </label>
                    );
                  })}
                </div>

                <div className="border-t border-slate-200 my-2" />

                {/* Overlay checkboxes */}
                <div className="space-y-1.5">
                  {[
                    { key: "padavine",    label: "Padavine (radar)" },
                    { key: "zgsLovisca",  label: "Meje lovišč" },
                    { key: "zgsLuo",      label: "Upravljavska območja (LUO)" },
                    { key: "zgsRezimi",   label: "Režimi lova" },
                    { key: "visokePreze", label: "Visoke preže" },
                    { key: "stozecVonja", label: "Raznos vonja" },
                    { key: "mojaLokacija",label: "Moja lokacija" },
                  ].map(({ key, label }) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 rounded px-1 py-0.5"
                      data-testid={`overlay-toggle-${key}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!showLayers[key]}
                        onChange={(e) => setShowLayers({ ...showLayers, [key]: e.target.checked })}
                        className="accent-primary"
                      />
                      <span className="text-slate-900 font-medium">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <button
                onClick={() => setLayerPickerOpen(true)}
                className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200 w-10 h-10 flex items-center justify-center hover:bg-white transition"
                aria-label="Odpri izbirnik slojev"
                data-testid="layer-picker-open"
              >
                <Layers className="h-5 w-5 text-slate-700" />
              </button>
            )}
          </div>

          {/* Weather widget - top right on map, shifted below layer picker */}
          {weather && showLayers.vremeWidget && (
            <div
              className="absolute right-3 pointer-events-none"
              style={{ zIndex: 9999, top: layerPickerOpen ? "310px" : "60px" }}
              data-testid="weather-map-temp"
            >
              <div className="bg-black/30 backdrop-blur-[2px] rounded-xl px-3 py-2 text-white">
                <div className="flex items-center gap-2">
                  <span className="text-2xl drop-shadow">{weather.ikona}</span>
                  <span className="text-2xl font-bold drop-shadow">{weather.temperatura}°C</span>
                </div>
                <div className="text-xs opacity-80">{weather.opis}</div>
                {weather.obcutena_temp != null && (
                  <div className="text-[10px] opacity-70">Občutena: {weather.obcutena_temp}°C</div>
                )}
                <div className="text-xs mt-1 border-t border-white/20 pt-1 space-y-0.5">
                  <div>Veter: {weather.veter_hitrost} km/h iz {weather.veter_smer}</div>
                  {weather.veter_sunek > 0 && (
                    <div className="text-orange-300">Sunki: {weather.veter_sunek} km/h</div>
                  )}
                  {weather.vonj_smer && (
                    <div className="text-yellow-300 font-medium">Vonj nese: {weather.vonj_smer}</div>
                  )}
                  <div className="opacity-70">Vlažnost: {weather.vlaznost}% | {weather.pritisk} hPa</div>
                  {weather.padavine_ura > 0 && (
                    <div className="text-blue-300">Padavine: {weather.padavine_ura} mm/h</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {isAddingMarker && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] bg-green-600 text-white px-4 py-2 rounded-full shadow-lg">
              Kliknite na karto za dodajanje oznake
            </div>
          )}
          {isDrawingPogon && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg">
              Klikajte za risanje linije pogona ({currentPogonPoints.length} točk)
            </div>
          )}
          {isPlacingStojisce && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] bg-red-600 text-white px-4 py-2 rounded-full shadow-lg animate-pulse text-sm">
              Pritisnite in zadržite za postavitev stojišča #{stojisc.length + 1}
            </div>
          )}
          {isPlacingGonjac && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] bg-green-600 text-white px-4 py-2 rounded-full shadow-lg animate-pulse text-sm">
              Pritisnite in povlecite v smer gonjača
            </div>
          )}

          {/* Animated radar controls */}
          {showLayers.padavine && radarFrames.length > 1 && (
            <div
              className="absolute bottom-4 left-4 z-[1000] bg-slate-900/85 backdrop-blur-sm text-white rounded-lg shadow-lg px-3 py-2 flex items-center gap-2 select-none"
              data-testid="radar-controls"
            >
              <button
                type="button"
                onClick={() => setRadarPlaying(p => !p)}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-white/15"
                title={radarPlaying ? "Ustavi" : "Predvajaj"}
                data-testid="radar-play-pause"
              >
                {radarPlaying ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                )}
              </button>
              <input
                type="range"
                min={0}
                max={radarFrames.length - 1}
                value={radarIndex}
                onChange={(e) => { setRadarPlaying(false); setRadarIndex(Number(e.target.value)); }}
                className="w-28 accent-blue-400"
                data-testid="radar-scrub"
              />
              <div className="text-xs font-mono tabular-nums min-w-[68px] text-right">
                {(() => {
                  const t = radarFrames[radarIndex]?.time;
                  if (!t) return "--:--";
                  const d = new Date(t * 1000);
                  const isFuture = t * 1000 > Date.now();
                  const hh = String(d.getHours()).padStart(2, "0");
                  const mm = String(d.getMinutes()).padStart(2, "0");
                  return (isFuture ? "+" : "") + `${hh}:${mm}`;
                })()}
              </div>
            </div>
          )}
          
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={true}
          >
            {/* Base layer - Satellite (Esri), Relief (OpenTopoMap) or OpenStreetMap */}
            {showLayers.satelit ? (
              <TileLayer
                attribution='Imagery &copy; <a href="https://www.esri.com/">Esri</a>'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
            ) : showLayers.relief ? (
              <TileLayer
                attribution='Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, <a href="http://viewfinderpanoramas.org">SRTM</a> · Style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
                url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                maxZoom={17}
              />
            ) : (
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            )}

            {/* ZGS WMS overlays (uradni podatki – meje lovišč, LUO, režimi) */}
            {showLayers.zgsLovisca && (
              <WMSTileLayer
                url="https://prostor.zgs.gov.si/geoserver/wms"
                layers="pregledovalnik:lovisca"
                format="image/png"
                transparent={true}
                version="1.1.1"
                opacity={0.8}
                zIndex={410}
              />
            )}
            {showLayers.zgsLuo && (
              <WMSTileLayer
                url="https://prostor.zgs.gov.si/geoserver/wms"
                layers="pregledovalnik:luo"
                format="image/png"
                transparent={true}
                version="1.1.1"
                opacity={0.5}
                zIndex={411}
              />
            )}
            {showLayers.zgsRezimi && (
              <WMSTileLayer
                url="https://prostor.zgs.gov.si/geoserver/wms"
                layers="pregledovalnik:lov_rezimi"
                format="image/png"
                transparent={true}
                version="1.1.1"
                opacity={0.6}
                zIndex={412}
              />
            )}
            
            {/* RainViewer precipitation radar overlay (animated loop) */}
            {showLayers.padavine && radarFrames.length > 0 && radarFrames[radarIndex] && (
              <TileLayer
                key={radarFrames[radarIndex].path}
                url={`https://tilecache.rainviewer.com${radarFrames[radarIndex].path}/512/{z}/{x}/{y}/1/1_1.png`}
                opacity={0.55}
                zIndex={400}
                maxNativeZoom={7}
                maxZoom={18}
              />
            )}
            
            
            <MapClickHandler onMapClick={handleMapClick} isActive={isAddingMarker || isDrawingPogon} />
            <ZgsFeatureInfoHandler enabled={showLayers.zgsLovisca && !isAddingMarker && !isDrawingPogon && !isPlacingStojisce && !isPlacingGonjac} />
            
            <LongPressHandler
              isPlacingStojisce={isPlacingStojisce}
              isPlacingGonjac={isPlacingGonjac}
              onPlaceStojisce={handlePlaceStojisce}
              onGonjacComplete={handleGonjacComplete}
              gonjacPreviewRef={gonjacPreviewRef}
            />
            
            {/* Center map on user location when button clicked */}
            <CenterMapOnLocation 
              location={userLocation} 
              shouldCenter={shouldCenterOnUser} 
              onCentered={() => setShouldCenterOnUser(false)} 
            />
            
            {/* Hunting ground boundaries */}
            {showLayers.meje && boundaries.map((boundary) => (
              <Polygon
                key={boundary.name}
                positions={boundary.coordinates}
                pathOptions={{
                  color: boundary.color,
                  fillColor: boundary.color,
                  fillOpacity: boundary.name === "RAKEK" ? 0.2 : 0.1,
                  weight: boundary.name === "RAKEK" ? 3 : 2
                }}
              >
                <Popup>
                  <strong>{boundary.label}</strong>
                </Popup>
              </Polygon>
            ))}
            
            {/* Markers */}
            {filteredMarkers.map((marker) => (
              <Marker
                key={marker.id}
                position={[marker.lat, marker.lng]}
                icon={getMarkerIcon(marker)}
              >
                <Popup>
                  <div className="min-w-[150px]">
                    <strong>{marker.naziv}</strong>
                    <p className="text-xs text-gray-500 capitalize">{marker.tip.replace("_", " ")}</p>
                    {marker.opis && <p className="text-sm mt-1">{marker.opis}</p>}
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="mt-2 w-full"
                        onClick={() => handleDeleteMarker(marker.id)}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Izbriši
                      </Button>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* High seats (Visoke preže) */}
            <HighSeatsLayer
              token={token}
              user={user}
              enabled={showLayers.visokePreze}
              refreshKey={highSeatsRefresh}
            />
            
            {/* Scent spread blob (independent of Moja lokacija toggle & of GPS availability).
                Falls back to map center if user GPS not available. */}
            {showLayers.stozecVonja && weather && weather.vonj_kot != null && (() => {
              const blobLat = userLocation?.lat ?? mapCenter[0];
              const blobLng = userLocation?.lng ?? mapCenter[1];
              const windSpeed = weather.veter_hitrost || 3;
              // Build smoke-like plume polygons drifting downwind
              const plume = generateScentCone(blobLat, blobLng, (weather.vonj_kot + 180) % 360, windSpeed, scentPhase);
              return (
                <>
                  {plume.map((layer, idx) => (
                    <Polygon
                      key={`smoke-${idx}`}
                      positions={layer.points}
                      pathOptions={{
                        color: layer.color,
                        fillColor: layer.color,
                        fillOpacity: layer.opacity,
                        opacity: layer.opacity * 0.6,
                        weight: 0,
                      }}
                      interactive={false}
                    />
                  ))}
                </>
              );
            })()}

            {/* User location marker */}
            {showLayers.mojaLokacija && userLocation && (
              <>
                <Marker
                  position={[userLocation.lat, userLocation.lng]}
                  icon={userLocationIcon}
                >
                  <Popup>
                    <strong>Moja lokacija</strong>
                    <p className="text-xs text-muted-foreground">
                      Natančnost: ~{Math.round(userLocation.accuracy)}m
                    </p>
                    {isSharingLocation && (
                      <p className="text-xs text-purple-600 font-medium mt-1">
                        Deljenje vklopljeno
                      </p>
                    )}
                  </Popup>
                </Marker>
                <Circle
                  center={[userLocation.lat, userLocation.lng]}
                  radius={userLocation.accuracy}
                  pathOptions={{ color: "#dc2626", fillColor: "#dc2626", fillOpacity: 0.1, weight: 1 }}
                />
                {/* Scent cone from user location - yellow-green, 1km */}
                {/* Scent blob moved OUTSIDE this block to be independent of Moja lokacija toggle */}
                
                {/* Distance rings */}
                {showLayers.razdalje && [100, 200, 300, 400].map(dist => (
                  <React.Fragment key={`ring-${dist}`}>
                    <Circle
                      center={[userLocation.lat, userLocation.lng]}
                      radius={dist}
                      pathOptions={{ color: '#ffffff', fillColor: 'transparent', fillOpacity: 0, weight: 2, opacity: 0.8, dashArray: '8 4' }}
                    />
                    <Marker
                      position={[userLocation.lat + (dist / 111320), userLocation.lng]}
                      icon={L.divIcon({
                        className: '',
                        html: `<div style="background:rgba(0,0,0,0.6);color:white;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:bold;white-space:nowrap;transform:translate(-50%,-50%);">${dist}m</div>`,
                        iconSize: [0, 0]
                      })}
                      interactive={false}
                    />
                  </React.Fragment>
                ))}
              </>
            )}
            
            {/* Other members' shared locations */}
            {isSharingLocation && otherMembersLocations.map((member) => {
              const isGonjac = member.hunt_role === "gonjac";
              const bgColor = isGonjac ? "#16a34a" : "#dc2626";
              const label = isGonjac ? "G" : "S";
              return (
                <Marker
                  key={member.user_id}
                  position={[member.lat, member.lng]}
                  icon={L.divIcon({
                    className: '',
                    html: `<div style="position:relative;"><div style="width:30px;height:30px;border-radius:50%;background:${bgColor};border:3px solid white;color:white;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:12px;box-shadow:0 2px 8px rgba(0,0,0,0.4);transform:translate(-50%,-50%);">${label}</div><div style="position:absolute;top:-8px;left:18px;background:${bgColor};color:white;padding:1px 6px;border-radius:4px;font-size:10px;white-space:nowrap;font-weight:bold;box-shadow:0 1px 3px rgba(0,0,0,0.3);">${member.user_ime}</div></div>`,
                    iconSize: [0, 0]
                  })}
                >
                  <Popup>
                    <div>
                      <strong style={{color: bgColor}}>{member.user_ime}</strong>
                      <p className="text-xs font-medium" style={{color: bgColor}}>
                        {isGonjac ? "Gonjač" : "Stojišče"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(member.timestamp).toLocaleTimeString('sl-SI')}
                      </p>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            
            {/* Pogon lines */}
            {showLayers.pogoni && pogonLines.map((pogon, idx) => (
              <Polyline
                key={pogon.id || idx}
                positions={pogon.points}
                pathOptions={{ color: "#dc2626", weight: 3, dashArray: "10, 10" }}
              >
                <Popup>
                  <div>
                    <strong>{pogon.naziv}</strong>
                    <p className="text-xs">Smer pogona</p>
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="mt-2 w-full"
                        onClick={() => handleDeletePogon(pogon.id)}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Izbriši
                      </Button>
                    )}
                  </div>
                </Popup>
              </Polyline>
            ))}
            
            {/* Current drawing pogon */}
            {currentPogonPoints.length > 0 && (
              <Polyline
                positions={currentPogonPoints}
                pathOptions={{ color: "#2563eb", weight: 3 }}
              />
            )}
            
            {/* Hunt plan: Stojišča (shooting stands) — only if pogoni layer enabled */}
            {showLayers.pogoni && stojisc.map((s, idx) => (
              <Marker
                key={`stojisce-${idx}`}
                position={[s.lat, s.lng]}
                icon={L.divIcon({
                  className: '',
                  html: `<div style="width:32px;height:32px;border-radius:50%;background:#dc2626;border:3px solid white;color:white;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:14px;box-shadow:0 2px 8px rgba(0,0,0,0.4);transform:translate(-50%,-50%);">${s.num}</div>`,
                  iconSize: [0, 0]
                })}
              >
                <Popup>
                  <div className="text-center">
                    <strong>Stojišče {s.num}</strong>
                    <br/>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="mt-2"
                      onClick={() => setStojisc(prev => prev.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="h-3 w-3 mr-1" /> Odstrani
                    </Button>
                  </div>
                </Popup>
              </Marker>
            ))}
            
            {/* Hunt plan: Gonjači — only if pogoni layer enabled */}
            {showLayers.pogoni && gonjaci.map((g, idx) => {
              const angle = Math.atan2(g.dirLng - g.lng, g.dirLat - g.lat);
              const arrowLen = 0.002;
              const ha = 25 * Math.PI / 180;
              const tipLat = g.lat + arrowLen * Math.cos(angle);
              const tipLng = g.lng + arrowLen * Math.sin(angle);
              return (
                <React.Fragment key={`gonjac-${idx}`}>
                  <Marker
                    position={[g.lat, g.lng]}
                    icon={L.divIcon({
                      className: '',
                      html: `<div style="width:28px;height:28px;border-radius:50%;background:#16a34a;border:3px solid white;color:white;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:12px;box-shadow:0 2px 8px rgba(0,0,0,0.4);transform:translate(-50%,-50%);">G</div>`,
                      iconSize: [0, 0]
                    })}
                  >
                    <Popup>
                      <div className="text-center">
                        <strong>Gonjač {idx + 1}</strong>
                        <br/>
                        <Button size="sm" variant="destructive" className="mt-2" onClick={() => setGonjaci(prev => prev.filter((_, i) => i !== idx))}>
                          <Trash2 className="h-3 w-3 mr-1" /> Odstrani
                        </Button>
                      </div>
                    </Popup>
                  </Marker>
                  {/* Direction arrow */}
                  <Polyline
                    positions={[[g.lat, g.lng], [tipLat, tipLng]]}
                    pathOptions={{ color: '#16a34a', weight: 3, opacity: 0.9 }}
                  />
                  <Polygon
                    positions={[
                      [tipLat, tipLng],
                      [tipLat - 0.0008 * Math.cos(angle - ha), tipLng - 0.0008 * Math.sin(angle - ha)],
                      [tipLat - 0.0008 * Math.cos(angle + ha), tipLng - 0.0008 * Math.sin(angle + ha)]
                    ]}
                    pathOptions={{ color: '#16a34a', fillColor: '#16a34a', fillOpacity: 1, weight: 1 }}
                  />
                </React.Fragment>
              );
            })}
            
            {/* Gonjač drag preview (live during long press + drag) */}
            <GonjacDragPreview gonjacPreviewRef={gonjacPreviewRef} />
          </MapContainer>

          {/* Locate FAB – enables "Moja lokacija" and centers map on user */}
          <button
            type="button"
            data-testid="map-locate-fab"
            title="Centriraj na mojo lokacijo"
            aria-label="Centriraj na mojo lokacijo"
            onClick={() => {
              if (!showLayers.mojaLokacija) {
                setShowLayers({ ...showLayers, mojaLokacija: true });
              }
              if (userLocation) {
                setShouldCenterOnUser(true);
              } else if (navigator.geolocation) {
                toast.info("Iščem vašo lokacijo…");
                navigator.geolocation.getCurrentPosition(
                  (pos) => {
                    const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                    setUserLocation(loc);
                    setShouldCenterOnUser(true);
                    setShowLayers((prev) => ({ ...prev, mojaLokacija: true }));
                  },
                  () => toast.error("Ni dostopa do lokacije. Preverite dovoljenja brskalnika."),
                  { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
                );
              } else {
                toast.error("Ta brskalnik ne podpira geolokacije.");
              }
            }}
            className="absolute z-[500] bottom-24 right-4 rounded-full shadow-lg
                       bg-red-600 hover:bg-red-700 text-white
                       h-14 w-14 flex items-center justify-center
                       transition-transform active:scale-95"
            style={{ boxShadow: "0 10px 30px -8px rgba(0,0,0,0.35)" }}
          >
            <Navigation className="h-6 w-6" />
          </button>
        </div>
      </Card>

      {/* Add Marker Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dodaj oznako na karto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tip oznake</Label>
              <Select
                value={newMarker.tip}
                onValueChange={(value) => setNewMarker({ ...newMarker, tip: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lokalno_ime">Lokalno ime</SelectItem>
                  <SelectItem value="km_kamen">SŽ km kamen</SelectItem>
                  <SelectItem value="opazovalnica">Opazovalnica</SelectItem>
                  <SelectItem value="krmisce">Krmišče</SelectItem>
                  <SelectItem value="lovska_koca">Lovska koča</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Naziv *</Label>
              <Input
                value={newMarker.naziv}
                onChange={(e) => setNewMarker({ ...newMarker, naziv: e.target.value })}
                placeholder="npr. Dolga njiva, Stojišče 1, Km 42"
              />
            </div>
            <div className="space-y-2">
              <Label>Opis (opcijsko)</Label>
              <Textarea
                value={newMarker.opis}
                onChange={(e) => setNewMarker({ ...newMarker, opis: e.target.value })}
                placeholder="Dodatne informacije..."
                rows={2}
              />
            </div>
            <div className="text-xs text-muted-foreground">
              Koordinate: {newMarker.lat.toFixed(6)}, {newMarker.lng.toFixed(6)}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Prekliči
            </Button>
            <Button onClick={handleSaveMarker}>
              <Save className="h-4 w-4 mr-1" />
              Shrani
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Drive Plan Dialog */}
      <Dialog open={savePlanDialogOpen} onOpenChange={setSavePlanDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Shrani načrt pogona</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Naziv pogona *</Label>
              <Input
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                placeholder="npr. Pogon Javornik, Pogon Slivnica"
                data-testid="plan-name-input"
              />
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <div>Stojišča: <span className="font-medium text-red-600">{stojisc.length}</span></div>
              <div>Gonjači: <span className="font-medium text-green-600">{gonjaci.length}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavePlanDialogOpen(false)}>
              Prekliči
            </Button>
            <Button onClick={handleSaveDrivePlan} data-testid="confirm-save-plan-btn">
              <Save className="h-4 w-4 mr-1" />
              Shrani
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HuntingMapPage;
